import { useEffect, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { axiosInstance } from '../../../helpers'
import { seasonDetailKey } from './leasingSeasonKeys'
import {
    SeasonEnrichmentAutomation,
    SeasonEnrichmentRunNowResponse,
} from '../types/leasingSeason.types'

export const seasonEnrichmentAutomationsKey = (
    seasonId: number | null | undefined,
) => ['season-enrichment-automations', seasonId] as const

function isInFlightStatus(
    status: SeasonEnrichmentAutomation['season_run_status'],
) {
    return status === 'queued' || status === 'running'
}

export function useSeasonEnrichmentAutomations(
    seasonId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    const query = useQuery({
        queryKey: seasonEnrichmentAutomationsKey(seasonId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                SeasonEnrichmentAutomation[]
            >(`/outreach/leasing-seasons/${seasonId}/enrichment-automations/`)
            return data
        },
        enabled: seasonId != null,
        refetchInterval: (data) =>
            isInFlightStatus(data?.[0]?.season_run_status) ? 5000 : false,
    })

    const isInFlight = useMemo(
        () => isInFlightStatus(query.data?.[0]?.season_run_status),
        [query.data],
    )

    useEffect(() => {
        if (!isInFlight) return
        queryClient.invalidateQueries({ queryKey: ['property'] })
    }, [isInFlight, query.dataUpdatedAt, queryClient])

    const toggleMutation = useMutation({
        mutationFn: async ({
            automationId,
            active,
        }: {
            automationId: number
            active: boolean
        }) => {
            const {
                data,
            } = await axiosInstance.patch<SeasonEnrichmentAutomation>(
                `/outreach/leasing-seasons/${seasonId}/enrichment-automations/${automationId}/`,
                { active },
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData<SeasonEnrichmentAutomation[]>(
                seasonEnrichmentAutomationsKey(seasonId),
                (old) =>
                    old?.map((row) => (row.id === data.id ? data : row)) ?? old,
            )
            if (seasonId != null) {
                queryClient.invalidateQueries({
                    queryKey: seasonDetailKey(seasonId),
                })
            }
        },
    })

    const runNowMutation = useMutation({
        mutationFn: async () => {
            const {
                data,
            } = await axiosInstance.post<SeasonEnrichmentRunNowResponse>(
                `/outreach/leasing-seasons/${seasonId}/run-enrichment-now/`,
            )
            return data
        },
        onSuccess: () => {
            toast.success(
                'Enrichment queued. Tokens deduct as each person is processed.',
            )
            if (seasonId != null) {
                queryClient.invalidateQueries({
                    queryKey: seasonEnrichmentAutomationsKey(seasonId),
                })
                queryClient.invalidateQueries({
                    queryKey: seasonDetailKey(seasonId),
                })
            }
            queryClient.invalidateQueries({ queryKey: ['property'] })
        },
        onError: (error: { response?: { data?: { detail?: string } } }) => {
            toast.error(
                error?.response?.data?.detail ||
                    'Could not run enrichment right now',
            )
        },
    })

    return {
        automations: query.data ?? [],
        isLoading: query.isLoading,
        toggleAutomation: toggleMutation.mutate,
        isToggling: toggleMutation.isLoading,
        runNow: runNowMutation.mutate,
        isRunning: runNowMutation.isLoading || isInFlight,
        isInFlight,
    }
}
